import { Palmtree, FolderTree } from 'lucide-react';
import { Header as HeaderPrimitive } from "./ui/header";
import ThemeToggle from "@/components/ThemeToggle";
import NutMessageToggle from "@/components/NutMessageToggle";
import NutMessageOverlay from "@/components/NutMessageOverlay";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "@/hooks/redux";
import { RootState } from "../store";
import DocumentTreeSidebar from "./DocumentTreeSidebar";

// Navigation items are either links or sidebar content.
const navItems: Array<{
  path: string;
  label: string;
  icon: React.ElementType | undefined;
  ariaLabel?: string;
} | { sidebarContent: React.ElementType }> = [
  { path: '/', label: 'Documents', icon: FolderTree },
  { sidebarContent: DocumentTreeSidebar }
];

export function Header({ className }: { className?: string }) {
  const location = useLocation();
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const appTitle = useAppSelector((state: RootState) => state.ui.customVariables['--app-title'] || 'Team Tiki');


  // When there is content to render in the sidebar, we force it to always render.
  const hasSidebarContent = navItems.some((item) => "sidebarContent" in item);

  return (
    <>
      <HeaderPrimitive className={cn("sticky top-0", className)} hasSidebarContent={hasSidebarContent}>
        <HeaderPrimitive.Left>
          <HeaderPrimitive.OverflowToggle />
<div className="font-semibold flex items-center">
              <Palmtree className="h-5 w-5 mr-2 text-primary" />
              <div className="font-semibold">{appTitle}</div>
              <span className="ml-1 text-xs text-muted-foreground">🌴</span>
          </div>
        </HeaderPrimitive.Left>
        <HeaderPrimitive.Center>
          {navItems.map((item, index) => {
            if ("sidebarContent" in item) {
              return (
                <HeaderPrimitive.SidebarContent key={index}>
                  <item.sidebarContent />
                </HeaderPrimitive.SidebarContent>
              );
            }
            const Icon = item.icon;
            // Mark as active if the current path matches
            const isActive = location.pathname === item.path;
            return (
              <HeaderPrimitive.Link
                key={item.path}
                to={item.path}
                aria-label={item.ariaLabel || item.label}
                className={cn(
                  "flex items-center gap-1 px-2 py-1 rounded-md transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                    : "hover:bg-muted"
                )}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/[\(\)]/g, '')}`}
                aria-current={isActive ? "page" : undefined}
              >
                {Icon && <Icon aria-hidden className="size-3" />}
                <span>{item.label}</span>
              </HeaderPrimitive.Link>
            );
          })}
        </HeaderPrimitive.Center>
        <HeaderPrimitive.Right>
          <NutMessageToggle onClick={() => setIsOverlayOpen(true)} />
          <ThemeToggle />
        </HeaderPrimitive.Right>
      </HeaderPrimitive>

      <NutMessageOverlay
        isOpen={isOverlayOpen}
        onClose={() => setIsOverlayOpen(false)}
      />
    </>
  );
}

export default Header;
